import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { quizScriptApi } from '@/api/quizScript'
import { InlineDebugId } from './InlineDebugId'
import { QUIZ_LOCATIONS, type QuizLocation } from './quizLocation'
import { QuizPreviewPanel } from './QuizPreviewPanel'

function cn(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ')
}

export function QuizPanel({ lessonId }: { lessonId: number }) {
  const [activeLocation, setActiveLocation] = useState<QuizLocation>('selected')
  const [selectedQuizId, setSelectedQuizId] = useState<string | null>(null)

  const handleLocationChange = (loc: QuizLocation) => {
    setActiveLocation(loc)
    setSelectedQuizId(null)
  }

  const { data: quizzes, isLoading } = useQuery({
    queryKey: ['inspection', 'quizzes', lessonId, activeLocation],
    queryFn: () => quizScriptApi.getContentQuizzes(lessonId, activeLocation),
    staleTime: 5 * 60 * 1000,
  })

  const locationCounts = useQuery({
    queryKey: ['inspection', 'quizCounts', lessonId],
    queryFn: async () => {
      const counts: Record<string, number> = {}
      const results = await Promise.all(
        QUIZ_LOCATIONS.map(async loc => {
          const data = await quizScriptApi.getContentQuizzes(lessonId, loc.value)
          return { key: loc.value, count: data.length }
        })
      )
      for (const r of results) counts[r.key] = r.count
      return counts
    },
    staleTime: 5 * 60 * 1000,
  })

  const hasQuizzes = !!quizzes && quizzes.length > 0

  return (
    <div>
      {/* Sticky 필터 헤더 (스크롤 시 상단 고정) */}
      <div className="sticky top-0 z-30 space-y-3 border-b border-slate-200 bg-slate-50/95 px-6 pb-3 pt-6 backdrop-blur">
        {/* Location 탭 */}
        <div className="flex flex-wrap gap-2">
          {QUIZ_LOCATIONS.map(loc => {
            const count = locationCounts.data?.[loc.value]
            return (
              <button
                key={loc.value}
                onClick={() => handleLocationChange(loc.value)}
                className={cn(
                  'flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all',
                  activeLocation === loc.value
                    ? 'bg-white shadow-sm ring-1 ring-slate-200 text-slate-900'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                )}
              >
                {loc.label}
                {count !== undefined && (
                  <span className={cn(
                    'rounded-full px-1.5 py-0.5 text-xs font-bold',
                    count > 0 ? loc.color : 'bg-slate-100 text-slate-400'
                  )}>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* 번호 chip 네비게이션 */}
        {hasQuizzes && (
          <div className="flex flex-wrap gap-1.5">
            {quizzes.map((quiz, i) => {
              const id = String(quiz.quizId)
              const selected = id === selectedQuizId
              return (
                <button
                  key={id}
                  onClick={() => setSelectedQuizId(prev => prev === id ? null : id)}
                  className={cn(
                    'w-7 h-7 inline-flex items-center justify-center text-xs font-medium rounded-full border transition-colors',
                    selected
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-amber-400 hover:bg-amber-50'
                  )}
                >
                  {i + 1}
                </button>
              )
            })}
            {selectedQuizId && (
              <button
                onClick={() => setSelectedQuizId(null)}
                className="px-2 h-7 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
              >
                전체
              </button>
            )}
          </div>
        )}
      </div>

      {/* 퀴즈 목록 본문 */}
      <div className="px-6 py-4">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : !hasQuizzes ? (
          <div className="py-12 text-sm text-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-400">
            이 위치에 퀴즈가 없습니다
          </div>
        ) : (
          <div className="space-y-3">
            {quizzes
              .filter(q => !selectedQuizId || String(q.quizId) === selectedQuizId)
              .map((quiz, i) => {
                const id = String(quiz.quizId)
                return (
                  <div key={id} className="p-3 bg-white rounded-lg border border-slate-200 space-y-2">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-400 font-mono">{i + 1}.</span>
                      <InlineDebugId quizId={id} />
                    </div>
                    <QuizPreviewPanel quizId={id} />
                  </div>
                )
              })}
          </div>
        )}
      </div>
    </div>
  )
}
